import React from "react";
import moment from "moment";
import { tt } from "../../../utils/i18n/i18n";
import KC_Table from "../../../designsystem/KC_Table/KC_Table";
import TestsID from "../../../utils/TestsID";
import { FromTo } from "./FromTo";
import TSUtils from "../../../utils/helpers/TSUtils";
import BillStatusSwitch from "./BillStatusSwitch";
import { Download } from "./Download";
import "../styles.scss";
import BillType from "@tootsweet/model/entities/enum/BillType";
import KC_NoData from "../../../components/KC_NoData/KC_NoData";
import KC_Heading2 from "../../../designsystem/typography/KC-Heading/KC_Heading2";
import BillDTO from "@tootsweet/model/datatransfer/BillDTO";
import BillJavaScriptDTO from "@tootsweet/model/datatransfer/BillDTO";
import CurrencySubUnitsMap from "../../../model/CurrencySubUnitsMap";
import { ManagedAccountDropdown } from "../../../components/ManagedAccountDropdown/ManagedAccountDropdown";
import { DEFAULT_CURRENCY } from "@tootsweet/model/enums/Currencies";
import NiceModal from "@ebay/nice-modal-react";
import PayBillModal from "./PayBillModal";

import BillStatus from "@tootsweet/model/entities/enum/BillStatus";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";

interface BillsTableComponentI {
  bills: BillJavaScriptDTO[];
  isSubscription?: boolean;
  currencySubUnits: CurrencySubUnitsMap;
  displayAccountSelector: boolean;
  businessUserId?: string;
  onBillPaid?: () => void;
}

export default function BillsTableComponent(props: BillsTableComponentI) {
  const handlePayNowClick = async (bill: BillJavaScriptDTO) => {
    if (!props.businessUserId) return;

    await NiceModal.show(PayBillModal, {
      bill,
      businessUserId: props.businessUserId,
      currencySubUnits: props.currencySubUnits,
      onSuccess: () => {
        if (props.onBillPaid) {
          props.onBillPaid();
        }
      }
    });
  };

  const isUnpaidBill = (status: BillStatus) => {
    return status === BillStatus.PaymentFailed || status === BillStatus.Pending;
  };

  return (
    <div>
      {props.displayAccountSelector && (
        <div className="d-flex flex-column mb-4">
          <ManagedAccountDropdown />
        </div>
      )}
      <div className="d-flex flex-row">
        <KC_Heading2
          text={
            props.isSubscription
              ? tt("bill_subscription_section_heading")
              : tt("bill_exceptional_section_heading")
          }
        />
      </div>
      {!props.bills?.length && (
        <div className="mt-2">
          <KC_NoData text={tt("no_invoices")} />
        </div>
      )}
      {!!props.bills?.length && (
        <div className="w-100 mt-4">
          <KC_Table
            id={TestsID.BILLS_TABLE}
            keyPrefix={"bills"}
            headers={
              <>
                <th className="bills-th-small">{tt("bill_number_th")}</th>
                {props.isSubscription ? (
                  <th className="bills-th-large">{tt("period")}</th>
                ) : (
                  <th className="bills-th-large">{tt("billing_date")}</th>
                )}
                <th>{tt("bill_details")}</th>
                <th className="bills-th-small">{tt("rising")}</th>
                <th className="bills-th-small">{tt("status")}</th>
                <th className="bills-th-small">{tt("bill_download_th")}</th>
              </>
            }
            children={
              <>
                {props.bills?.map((bill: BillDTO, index: number) => {
                  const isUnpaid = isUnpaidBill(bill.status);
                  return (
                    <tr key={`bill${index}`}>
                      <td>
                        {bill.monthAndYear}-{bill.number}
                      </td>
                      {props.isSubscription ? (
                        <td>
                          <FromTo
                            isSubscription={props.isSubscription}
                            bill={bill}
                          />
                        </td>
                      ) : (
                        <td>
                          {moment(bill.billingDate).format(
                            tt("date_format_full_date")
                          )}
                        </td>
                      )}
                      <td>
                        {bill.billType === BillType.Subscription
                          ? bill.subscriptionDisplayName
                          : bill.items?.map((item) => (
                              <span className="d-block">{item.label}</span>
                            ))}
                      </td>
                      <td>
                        {TSUtils.formatAmount(
                          bill.amount,
                          props.currencySubUnits[
                            bill.currency || DEFAULT_CURRENCY
                          ],
                          false,
                          bill.currency || DEFAULT_CURRENCY
                        )}
                      </td>
                      <td className="d-flex flex-row">
                        {!isUnpaid && <BillStatusSwitch status={bill.status} />}
                        {isUnpaid && (
                          <KC_Button
                            size={KC_ButtonSize.Small}
                            className="text-white"
                            isPrimary={true}
                            text={tt("pay")}
                            onClick={() => handlePayNowClick(bill)}
                          />
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-row justify-content-center align-items-center gap-sm">
                          <Download bill={bill} loading={index === 0} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </>
            }
          />
        </div>
      )}
    </div>
  );
}
